import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Avatar, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Refresh, CheckCircle, Cancel, EventBusy, Timer, Warning, CameraAlt } from '@mui/icons-material';
import { InvokeApi, apiList, getCachedDepartments } from '../../services/ApiService';
import { Config } from '../../config/Config';
import AsyncStorage from '../../services/AsyncStorage';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';

const STATUSES = [
  { key: 'Present', label: 'Present', icon: '✅', bg: '#e8f5e9', color: '#2e7d32' },
  { key: 'Absent', label: 'Absent', icon: '❌', bg: '#ffebee', color: '#c62828' },
  { key: 'Late', label: 'Fine', icon: '⏰', bg: '#f3e5f5', color: '#6a1b9a' },
  { key: 'Half Day', label: 'Half Day', icon: '⏰', bg: '#f5f5f5', color: '#4f2f1e' },
  { key: 'Leave', label: 'Leave', icon: '🏖', bg: '#fff3e0', color: '#e65100' },
  { key: 'Week Off', label: 'Week Off', icon: '📅', bg: '#e0f2f1', color: '#00695c' },
  { key: 'Not Marked', label: 'Not Marked', icon: '❓', bg: '#f5f5f5', color: '#757575' },
];

const MARK_STATUSES = ['Present', 'Absent', 'Late', 'Half Day', 'Leave', 'Week Off'];

const fmtTime = (t) => t ? dayjs(t).format('hh:mm A') : '—';

export default function Attendance() {
  const { user } = useAuth();
  const isHR = user?.role === 'HR';
  const hrDept = user?.department || '';

  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [activeTab, setActiveTab] = useState('Present');
  // HR locked to their dept; Admin defaults to 'All'
  const [dept, setDept] = useState(isHR ? hrDept : 'All');
  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [adminId, setAdminId] = useState('1');

  const [markDialog, setMarkDialog] = useState(false);
  const [markForm, setMarkForm] = useState({ studentId: '', status: 'Present', date: '', check_in_time: '', check_out_time: '' });
  const [marking, setMarking] = useState(false);
  const [rowMarkDialog, setRowMarkDialog] = useState(false);
  const [rowMarkForm, setRowMarkForm] = useState({ studentId: '', name: '', status: 'Present', date: '', check_in_time: '', check_out_time: '' });
  const [rowMarking, setRowMarking] = useState(false);
  const [deductDialog, setDeductDialog] = useState(false);
  const [deductEmp, setDeductEmp] = useState(null);
  const [deductMinutes, setDeductMinutes] = useState('');
  const [deductReason, setDeductReason] = useState('');
  const [deducting, setDeducting] = useState(false);

  // Fine dialog
  const [fineDialog, setFineDialog] = useState(false);
  const [fineEmp, setFineEmp] = useState(null);
  const [fineForm, setFineForm] = useState({ fine_amount: '', fine_reason: '', fine_date: dayjs().format('YYYY-MM-DD') });
  const [savingFine, setSavingFine] = useState(false);

  // Week Off dept picker dialog (same pattern as absentDeptDialog)
  const [weekOffDeptDialog, setWeekOffDeptDialog] = useState(false);
  const [weekOffDeptPick, setWeekOffDeptPick] = useState('');


  // Mark Absent dept picker (for admin when dept = 'All')
  const [absentDeptDialog, setAbsentDeptDialog] = useState(false);
  const [absentDeptPick, setAbsentDeptPick] = useState('');

  // Scan log dialog
  const [scanDialog, setScanDialog] = useState(false);
  const [scanEmp, setScanEmp] = useState(null);
  const [scanLogs, setScanLogs] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const hrdepts = hrDept.split(',');

  useEffect(() => {
    const init = async () => {
      const id = await AsyncStorage.getItem('admin_id');
      if (id) setAdminId(id);
      // HR only sees their own dept — no need to load all departments
      // if (isHR) {
      //   setDepartments(hrDept ? [{ ID: 'hr', department: hrDept }] : []);
      // } else {
      const data = await getCachedDepartments(Config.main_url);
      setDepartments(data || []);
      // }
    };
    init();
  }, []);

  // ── Single call replaces 7+N calls ──────────────────────────────────────
  // getDailyAttendanceSummary returns counts + all rows grouped by status in 1 DB query
  const [summaryCache, setSummaryCache] = useState(null); // cache for tab switches

  const loadSummary = useCallback(async () => {
    setLoading(true);
    const formatted = dayjs(date).format('DD/MM/YYYY');
    const data = await InvokeApi(Config.main_url + apiList.getDailyAttendanceSummary, {
      date: formatted,
      department: dept,
    });

    if (!data || data.code !== '200') {
      setLoading(false);
      return;
    }

    // Build counts from response
    const c = { ...(data.counts || {}) };
    setCounts(c);
    setSummaryCache(data); // cache so tab switches don't re-fetch

    // Set records for current active tab
    const rows = data.summary?.[activeTab] || [];
    setRecords(rows);
    setLoading(false);
  }, [date, dept, activeTab]);

  // When only tab changes — use cached summary, no API call
  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    if (summaryCache) {
      const rows = summaryCache.summary?.[tab] || [];
      setRecords(rows);
    }
  }, [summaryCache]);

  useEffect(() => { loadSummary(); }, [date, dept]);

  // Tab switch: use cache if available, else reload
  useEffect(() => {
    if (summaryCache) {
      const rows = summaryCache.summary?.[activeTab] || [];
      setRecords(rows);
    } else {
      loadSummary();
    }
  }, [activeTab]);

  const handleRefresh = () => { setSummaryCache(null); loadSummary(); };

  const handleMark = async () => {
    if (!markForm.studentId || !markForm.date) { alert('Employee ID and date are required'); return; }
    setMarking(true);
    const payload = {
      id: markForm.studentId,
      date: dayjs(markForm.date).format('YYYY-MM-DD'),
      status: markForm.status,
      marked_by: adminId,
      marked_by_name: user?.name || 'Admin',
    };
    if (markForm.check_in_time) payload.check_in_time = `${markForm.date}T${markForm.check_in_time}:00`;
    if (markForm.check_out_time) payload.check_out_time = `${markForm.date}T${markForm.check_out_time}:00`;
    const data = await InvokeApi(Config.main_url + apiList.markAttendance, payload);
    setMarking(false);
    if (data?.code === '200') { setMarkDialog(false); handleRefresh(); }
    else alert(data?.message || 'Failed to mark attendance');
  };

  const handleMarkRow = async (empId, status) => {
    const data = await InvokeApi(Config.main_url + apiList.markAttendance, {
      id: empId,
      date: dayjs(date).format('YYYY-MM-DD'),
      status,
      marked_by: adminId,
      marked_by_name: user?.name || 'Admin',
    });
    if (data?.code === '200') handleRefresh();
    else alert(data?.message || 'Failed');
  };

  const openRowMark = (r) => {
    const existingIn = r.check_in_time ? dayjs(r.check_in_time).format('HH:mm') : '';
    const existingOut = r.check_out_time ? dayjs(r.check_out_time).format('HH:mm') : '';
    setRowMarkForm({
      studentId: String(r.student_id || r.ID),
      name: r.name,
      status: (activeTab === 'Not Marked' ? 'Present' : activeTab) || r.status,
      date,
      check_in_time: existingIn,
      check_out_time: existingOut,
    });
    setRowMarkDialog(true);
  };

  const handleRowMarkSubmit = async () => {
    if (!rowMarkForm.studentId || !rowMarkForm.date) { alert('Employee ID and date required'); return; }
    setRowMarking(true);
    const payload = {
      id: rowMarkForm.studentId,
      date: dayjs(rowMarkForm.date).format('YYYY-MM-DD'),
      status: rowMarkForm.status,
      marked_by: adminId,
      marked_by_name: user?.name || 'Admin',
    };
    if (rowMarkForm.check_in_time) payload.check_in_time = `${rowMarkForm.date}T${rowMarkForm.check_in_time}:00`;
    if (rowMarkForm.check_out_time) payload.check_out_time = `${rowMarkForm.date}T${rowMarkForm.check_out_time}:00`;
    const data = await InvokeApi(Config.main_url + apiList.markAttendance, payload);
    setRowMarking(false);
    if (data?.code === '200') { setRowMarkDialog(false); handleRefresh(); }
    else alert(data?.message || 'Failed to mark attendance');
  };

  const openFineDialog = (r) => {
    setFineEmp(r);
    setFineForm({ fine_amount: '', fine_reason: '', fine_date: date });
    setFineDialog(true);
  };

  const handleAddFine = async () => {
    if (!fineForm.fine_amount) { alert('Fine amount required'); return; }
    setSavingFine(true);
    const data = await InvokeApi(Config.main_url + apiList.addFine, {
      employee_id: fineEmp.student_id || fineEmp.ID,
      fine_amount: fineForm.fine_amount,
      fine_reason: fineForm.fine_reason,
      fine_date: fineForm.fine_date,
      added_by: adminId,
      added_by_name: user?.name || 'Admin',
    });
    setSavingFine(false);
    if (data?.code === '200') { setFineDialog(false); alert('Fine added!'); }
    else alert(data?.message || 'Failed');
  };

  const openScanLogs = async (r) => {
    setScanEmp(r);
    setScanLogs([]);
    setScanDialog(true);
    setScanLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getScanLogs, {
      employee_id: r.student_id || r.ID,
      from_date: date,
      to_date: date,
      limit: 20,
    });
    setScanLogs(data?.scan_logs || []);
    setScanLoading(false);
  };

  const handleMarkWeekOff = async () => {
    if (dept === 'All') {
      // Admin with All selected — show dept picker dialog
      setWeekOffDeptPick('');
      setWeekOffDeptDialog(true);
      return;
    }
    const targetDept = isHR ? hrDept : dept;
    if (!window.confirm(`Mark Week Off for all unmarked in "${targetDept}" on ${dayjs(date).format('DD MMM YYYY')}?\n\nEmployees already marked (Present, Absent, Leave, etc.) will NOT be changed.`)) return;
    const data = await InvokeApi(Config.main_url + apiList.markWeekOff, {
      date: dayjs(date).format('YYYY-MM-DD'), department: targetDept, marked_by: adminId,
    });
    if (data?.code === '200') {
      const d = data.data;
      alert(`Week Off marked for ${d?.new_records ?? 0} employee(s).\n${d?.skipped ?? 0} already marked — skipped.`);
      handleRefresh();
    } else alert(data?.message || 'Failed');
  };

  const handleMarkWeekOffConfirm = async () => {
    if (!weekOffDeptPick) { alert('Please select a department'); return; }
    setWeekOffDeptDialog(false);
    if (!window.confirm(`Mark Week Off for all unmarked in "${weekOffDeptPick}" on ${dayjs(date).format('DD MMM YYYY')}?\n\nEmployees already marked will NOT be changed.`)) return;
    const data = await InvokeApi(Config.main_url + apiList.markWeekOff, {
      date: dayjs(date).format('YYYY-MM-DD'), department: weekOffDeptPick, marked_by: adminId,
    });
    if (data?.code === '200') {
      const d = data.data;
      alert(`Week Off marked for ${d?.new_records ?? 0} employee(s).\n${d?.skipped ?? 0} already marked — skipped.`);
      handleRefresh();
    } else alert(data?.message || 'Failed');
  };

  const handleMarkAbsent = async () => {
    // HR: dept is already locked — confirm and mark directly
    if (dept === 'All') {
      // Admin with All selected — show dept picker dialog
      setAbsentDeptPick('');
      setAbsentDeptDialog(true);
      return;
    }
    const targetDept = isHR ? hrDept : dept;
    if (!window.confirm(`Mark Absent for all unmarked in "${targetDept}" for ${dayjs(date).format('DD MMM YYYY')}?`)) return;
    const data = await InvokeApi(Config.main_url + apiList.markAbsent, {
      date: dayjs(date).format('YYYY-MM-DD'),
      department: targetDept,
    });
    if (data?.code === '200') handleRefresh();
    else alert(data?.message || 'Failed');
  };

  const handleMarkAbsentConfirm = async () => {
    if (!absentDeptPick) { alert('Please select a department'); return; }
    setAbsentDeptDialog(false);
    if (!window.confirm(`Mark Absent for all unmarked in "${absentDeptPick}" for ${dayjs(date).format('DD MMM YYYY')}?`)) return;
    const data = await InvokeApi(Config.main_url + apiList.markAbsent, {
      date: dayjs(date).format('YYYY-MM-DD'),
      department: absentDeptPick,
    });
    if (data?.code === '200') handleRefresh();
    else alert(data?.message || 'Failed');
  };

  const handleDeductionReduce = async () => {
    const mins = parseInt(deductMinutes);
    if (!mins || mins <= 0) { alert('Enter valid minutes'); return; }
    if (deductEmp?.total_deduction_minutes && mins > deductEmp.total_deduction_minutes) {
      alert(`Cannot exceed total deduction (${deductEmp.total_deduction_formatted})`);
      return;
    }
    setDeducting(true);
    const data = await InvokeApi(Config.main_url + apiList.reduceEmployeeDeduction, {
      employee_id: deductEmp.student_id,
      date: dayjs(date).format('YYYY-MM-DD'),
      reduction_minutes: mins,
      reason: deductReason || 'Manual adjustment',
      admin_id: adminId,
    });
    setDeducting(false);
    if (data?.code === '200') {
      setDeductDialog(false); setDeductMinutes(''); setDeductReason('');
      handleRefresh();
    } else alert(data?.message || 'Failed to reduce deduction');
  };

  const activeStatus = STATUSES.find(s => s.key === activeTab);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Attendance</Typography>
          <Typography variant="body2" color="text.secondary">{dayjs(date).format('dddd, MMMM D, YYYY')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={() => { setMarkForm({ studentId: '', status: 'Present', date }); setMarkDialog(true); }}
            startIcon={<CheckCircle />} sx={{ borderRadius: 2, color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600 }}>
            Mark
          </Button>
          <Button variant="outlined" size="small" onClick={ isHR ? () => setAbsentDeptDialog(true) : handleMarkAbsent}
            startIcon={<Cancel />} sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontWeight: 600 }}>
            Mark Absent
          </Button>
          <Button variant="outlined" size="small" onClick={handleMarkWeekOff}
            startIcon={<EventBusy />} sx={{ borderRadius: 2, color: '#00695c', borderColor: '#00695c', fontWeight: 600 }}>
            Week Off
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField type="date" size="small" label="Date" value={date}
            onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          {isHR ? (
            // <Chip label={hrDept} sx={{ background: '#fce4ec', color: '#880e4f', fontWeight: 700 }} />
            <TextField
              select
              size="small"
              label="Department"
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {/* <MenuItem value="All">All Departments</MenuItem> */}

              {hrdepts.map((d, index) => (
                <MenuItem key={index} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField select size="small" label="Department" value={dept}
              onChange={(e) => setDept(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="All">All Departments</MenuItem>
              {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
            </TextField>
          )}
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <Box key={s.key} onClick={() => switchTab(s.key)} sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
            borderRadius: 10, cursor: 'pointer', border: '1.5px solid',
            borderColor: activeTab === s.key ? s.color : '#e8eaf6',
            background: activeTab === s.key ? s.bg : '#fff',
            transition: 'all 0.2s', '&:hover': { background: s.bg },
          }}>
            {/* <Typography fontSize={13}>{s.icon}</Typography> */}
            <Typography fontSize={13} fontWeight={700} color={activeTab === s.key ? s.color : 'text.secondary'}>{s.label}</Typography>
            <Chip label={counts[s.key] ?? '—'} size="small"
              sx={{ background: activeTab === s.key ? s.color : '#f0f0f0', color: activeTab === s.key ? '#fff' : '#666', fontWeight: 700, fontSize: 11, height: 20 }} />
          </Box>
        ))}
      </Box>

      {/* Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontSize={14} fontWeight={700} color={activeStatus?.color}> {activeTab}</Typography>
          <Chip label={`${records.length} records`} size="small" sx={{ background: '#f5f6fa', color: 'text.secondary', fontWeight: 600 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Dept</TableCell>
                  {activeTab !== 'Not Marked' && <>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Check In</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Check Out</TableCell>
                  </>}
                  {(activeTab === 'Late') && (
                    <TableCell sx={{ fontWeight: 700, color: '#c62828', fontSize: 12 }}>Deduction</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Status / Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No {activeTab} records for {dayjs(date).format('DD MMM YYYY')}
                  </TableCell></TableRow>
                ) : records.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 30, height: 30, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                          {r.name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} fontSize={12}>{r.name}</Typography>
                          <Typography fontSize={10} color="text.secondary">ID: {r.student_id || r.ID}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.department || 'All'}</TableCell>

                    {activeTab !== 'Not Marked' && <>
                      <TableCell sx={{ fontSize: 12 }}>{fmtTime(r.check_in_time)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtTime(r.check_out_time)}</TableCell>
                    </>}

                    {/* Deduction info for Present/Late */}
                    {(activeTab === 'Late') && (
                      <TableCell>
                        {r.has_deduction || r.total_deduction_minutes > 0 ? (
                          <Box>
                            <Chip label={r.total_deduction_formatted || '—'} size="small"
                              sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10, mb: 0.3 }} />
                            {r.late_checkin_minutes > 0 && (
                              <Typography fontSize={10} color="#e65100">Late: {r.late_checkin_formatted}</Typography>
                            )}
                            {r.early_checkout_minutes > 0 && (
                              <Typography fontSize={10} color="#c62828">Early out: {r.early_checkout_formatted}</Typography>
                            )}
                            {r.reduction_minutes > 0 && (
                              <Typography fontSize={10} color="#2e7d32">Reduced: -{r.reduction_minutes}m</Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography fontSize={11} color="text.disabled">No deduction</Typography>
                        )}
                      </TableCell>
                    )}

                    {/* Actions */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {activeTab === 'Not Marked' ? (
                          <>
                            {/* {['Present', 'Absent', 'Leave', 'Week Off'].map(s => (
                              <Chip key={s} label={s} size="small" onClick={() => handleMarkRow(r.student_id || r.ID, s)}
                                sx={{
                                  cursor: 'pointer', fontWeight: 600, fontSize: 10,
                                  background: s === 'Present' ? '#e8f5e9' : s === 'Absent' ? '#ffebee' : s === 'Leave' ? '#fff3e0' : '#e0f2f1',
                                  color: s === 'Present' ? '#2e7d32' : s === 'Absent' ? '#c62828' : s === 'Leave' ? '#e65100' : '#00695c',
                                  '&:hover': { opacity: 0.8 },
                                }} />
                            ))} */}
                          </>
                        ) : (activeTab === 'Present' || activeTab === 'Late') && (r.has_deduction || r.total_deduction_minutes > 0) ? (
                          <Tooltip title="Reduce Deduction">
                            <Button size="small" variant="outlined" startIcon={<Timer fontSize="small" />}
                              onClick={() => { setDeductEmp(r); setDeductMinutes(''); setDeductReason(''); setDeductDialog(true); }}
                              sx={{ borderRadius: 1.5, color: '#1a237e', borderColor: '#1a237e', fontSize: 10, fontWeight: 700 }}>
                              Reduce
                            </Button>
                          </Tooltip>
                        ) : (
                          <Chip label={r.status || activeTab} size="small"
                            sx={{ background: activeStatus?.bg, color: activeStatus?.color, fontWeight: 600, fontSize: 10 }} />
                        )}
                        {/* Per-row Mark button */}
                        <Tooltip title="Mark / Update Attendance">
                          <Button size="small" variant="contained" onClick={() => openRowMark(r)}
                            sx={{ borderRadius: 1.5, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 10, fontWeight: 700, minWidth: 0, px: 1.2, py: 0.4 }}>
                            Mark
                          </Button>
                        </Tooltip>
                        {/* Add Fine button */}
                        <Tooltip title="Add Fine">
                          <Button size="small" variant="outlined" startIcon={<Warning sx={{ fontSize: '12px !important' }} />}
                            onClick={() => openFineDialog(r)}
                            sx={{ borderRadius: 1.5, color: '#c62828', borderColor: '#c62828', fontSize: 10, fontWeight: 700, minWidth: 0, px: 1, py: 0.4 }}>
                            Fine
                          </Button>
                        </Tooltip>
                        {/* Scan Log button */}
                        <Tooltip title="View Scan Logs">
                          <IconButton size="small" onClick={() => openScanLogs(r)}
                            sx={{ color: '#00695c', border: '1px solid #b2dfdb', borderRadius: 1.5, p: 0.4 }}>
                            <CameraAlt sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Mark Attendance Dialog */}
      <Dialog open={markDialog} onClose={() => setMarkDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Mark Attendance</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Employee ID *" value={markForm.studentId}
                onChange={(e) => setMarkForm(p => ({ ...p, studentId: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Date *" value={markForm.date}
                onChange={(e) => setMarkForm(p => ({ ...p, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Status" value={markForm.status}
                onChange={(e) => setMarkForm(p => ({ ...p, status: e.target.value }))}>
                {MARK_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="time" label="Check In Time"
                value={markForm.check_in_time}
                onChange={(e) => setMarkForm(p => ({ ...p, check_in_time: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="time" label="Check Out Time"
                value={markForm.check_out_time}
                onChange={(e) => setMarkForm(p => ({ ...p, check_out_time: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setMarkDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleMark} variant="contained" disabled={marking}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {marking ? <CircularProgress size={20} color="inherit" /> : 'Mark'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deduction Reduction Dialog */}
      <Dialog open={deductDialog} onClose={() => setDeductDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timer /> Reduce Deduction
        </DialogTitle>
        <DialogContent dividers>
          {deductEmp && (
            <Box sx={{ mb: 2, p: 1.5, background: '#f5f6fa', borderRadius: 2, borderLeft: '3px solid #1a237e' }}>
              <Typography fontWeight={700} fontSize={14}>{deductEmp.name}</Typography>
              <Typography fontSize={12} color="text.secondary">ID: {deductEmp.student_id}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${deductEmp.total_deduction_formatted || deductEmp.total_deduction_minutes + 'm'}`}
                  size="small" sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10 }} />
                {deductEmp.late_checkin_minutes > 0 && (
                  <Chip label={`Late: ${deductEmp.late_checkin_formatted}`} size="small"
                    sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: 10 }} />
                )}
                {deductEmp.early_checkout_minutes > 0 && (
                  <Chip label={`Early out: ${deductEmp.early_checkout_formatted}`} size="small"
                    sx={{ background: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: 10 }} />
                )}
                {deductEmp.check_in_time && (
                  <Chip label={`In: ${fmtTime(deductEmp.check_in_time)}`} size="small"
                    sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 10 }} />
                )}
                {deductEmp.check_out_time && (
                  <Chip label={`Out: ${fmtTime(deductEmp.check_out_time)}`} size="small"
                    sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} />
                )}
              </Box>
            </Box>
          )}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reduce by (minutes) *" type="number"
                value={deductMinutes} onChange={(e) => setDeductMinutes(e.target.value)}
                helperText={deductMinutes ? `= ${Math.floor(deductMinutes / 60)}h ${deductMinutes % 60}m` : ''} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason (optional)" value={deductReason}
                onChange={(e) => setDeductReason(e.target.value)} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDeductDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleDeductionReduce} variant="contained" disabled={deducting}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {deducting ? <CircularProgress size={20} color="inherit" /> : 'Apply Reduction'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Row Mark / Update Dialog */}
      <Dialog open={rowMarkDialog} onClose={() => setRowMarkDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          Mark Attendance — {rowMarkForm.name}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Date *" value={rowMarkForm.date}
                onChange={(e) => setRowMarkForm(p => ({ ...p, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Status" value={rowMarkForm.status}
                onChange={(e) => setRowMarkForm(p => ({ ...p, status: e.target.value }))}>
                {MARK_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="time" label="Check In Time"
                value={rowMarkForm.check_in_time}
                onChange={(e) => setRowMarkForm(p => ({ ...p, check_in_time: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="time" label="Check Out Time"
                value={rowMarkForm.check_out_time}
                onChange={(e) => setRowMarkForm(p => ({ ...p, check_out_time: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setRowMarkDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleRowMarkSubmit} variant="contained" disabled={rowMarking}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {rowMarking ? <CircularProgress size={20} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Fine Dialog */}
      <Dialog open={fineDialog} onClose={() => setFineDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#c62828' }}>
          Add Fine — {fineEmp?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Fine Amount (₹) *" type="number"
                value={fineForm.fine_amount} onChange={e => setFineForm(p => ({ ...p, fine_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Fine Date *"
                value={fineForm.fine_date} onChange={e => setFineForm(p => ({ ...p, fine_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason" multiline rows={2}
                value={fineForm.fine_reason} onChange={e => setFineForm(p => ({ ...p, fine_reason: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setFineDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAddFine} variant="contained" disabled={savingFine}
            sx={{ background: 'linear-gradient(135deg,#c62828,#e53935)', borderRadius: 2, fontWeight: 700 }}>
            {savingFine ? <CircularProgress size={20} color="inherit" /> : 'Add Fine'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan Logs Dialog */}
      <Dialog open={scanDialog} onClose={() => setScanDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#00695c', borderBottom: '1px solid #f0f0f0' }}>
          Scan Logs — {scanEmp?.name} · {dayjs(date).format('DD MMM YYYY')}
        </DialogTitle>
        <DialogContent dividers>
          {scanLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#00695c' }} /></Box>
          ) : scanLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CameraAlt sx={{ fontSize: 48, color: '#e0f2f1', mb: 1 }} />
              <Typography color="text.secondary">No scan logs for this day</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              {scanLogs.map(log => {
                const isCheckIn = log.scan_type === 'check_in';
                const isCheckOut = log.scan_type === 'check_out';
                const isFaceScan = !!log.captured_image_url;

                // Color + label per scan type
                const typeStyles = {
                  check_in: { color: '#2e7d32', bg: '#e8f5e9', label: '↑ Check In' },
                  check_out: { color: '#e65100', bg: '#fff3e0', label: '↓ Check Out' },
                  absent: { color: '#c62828', bg: '#ffebee', label: '✗ Absent' },
                  leave: { color: '#e65100', bg: '#fff3e0', label: '🏖 Leave' },
                  week_off: { color: '#00695c', bg: '#e0f2f1', label: '📅 Week Off' },
                  present: { color: '#2e7d32', bg: '#e8f5e9', label: '✓ Present' },
                  late: { color: '#6a1b9a', bg: '#f3e5f5', label: '⏰ Late' },
                };
                const ts = typeStyles[log.scan_type] || { color: '#555', bg: '#f5f5f5', label: log.scan_type };
                const typeColor = ts.color;
                const typeBg = ts.bg;
                return (
                  <Box key={log.ID} sx={{
                    display: 'flex', gap: 2, p: 2, borderRadius: 2,
                    border: `1px solid ${typeColor}20`,
                    borderLeft: `4px solid ${typeColor}`,
                    background: '#fff',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                    alignItems: 'flex-start',
                  }}>
                    {/* Captured image — only if exists */}
                    {isFaceScan && (
                      <Box
                        component="img"
                        src={log.captured_image_url}
                        alt="scan"
                        onClick={() => window.open(log.captured_image_url, '_blank')}
                        sx={{
                          width: 64, height: 64, borderRadius: 2, objectFit: 'cover',
                          border: `2px solid ${typeColor}40`, cursor: 'pointer', flexShrink: 0,
                        }}
                      />
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Type + time */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={ts.label}
                          size="small"
                          sx={{ background: typeBg, color: typeColor, fontWeight: 800, fontSize: 11 }}
                        />
                        <Typography fontSize={16} fontWeight={800} color={typeColor}>
                          {log.scan_time ? dayjs(log.scan_time).format('hh:mm A') : '—'}
                        </Typography>
                      </Box>

                      {/* Source */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CameraAlt sx={{ fontSize: 13, color: isFaceScan ? '#00695c' : '#9e9e9e' }} />
                        <Typography fontSize={12} fontWeight={600} color={isFaceScan ? '#00695c' : 'text.secondary'}>
                          {isFaceScan
                            ? `Scanned by ${log.employee_name || 'Employee'}`
                            : `Marked by ${(log.device_info || 'Admin').replace(/^Marked by\s*/i, '')}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setScanDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Mark Absent — Department Picker (Admin only, when dept = 'All') */}
      <Dialog open={absentDeptDialog} onClose={() => setAbsentDeptDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#c62828', borderBottom: '1px solid #f0f0f0' }}>
          Mark Absent — Select Department
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Choose which department to mark absent for {dayjs(date).format('DD MMM YYYY')}
          </Typography>
          {isHR ? (
            // {/* <Chip label={hrDept} sx={{ background: '#fce4ec', color: '#880e4f', fontWeight: 700 }} /> */}
            <TextField
              select fullWidth size="small" label="Department *"
              value={absentDeptPick}
              onChange={(e) => setAbsentDeptPick(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {/* <MenuItem value="All">All Departments</MenuItem> */}

              {hrdepts.map((d, index) => (
                <MenuItem key={index} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>)
            : (
              <TextField
                select fullWidth size="small" label="Department *"
                value={absentDeptPick}
                onChange={e => setAbsentDeptPick(e.target.value)}>
                <MenuItem value="">— Select Department —</MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>
                ))}
              </TextField>)}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setAbsentDeptDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleMarkAbsentConfirm} variant="contained"
            sx={{ background: 'linear-gradient(135deg,#c62828,#e53935)', borderRadius: 2, fontWeight: 700 }}>
            Mark Absent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Week Off — Department Picker (Admin only, when dept = 'All') */}
      <Dialog open={weekOffDeptDialog} onClose={() => setWeekOffDeptDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#00695c', borderBottom: '1px solid #f0f0f0' }}>
          Week Off — Select Department
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Only employees with <strong>no attendance record yet</strong> on {dayjs(date).format('DD MMM YYYY')} will be marked Week Off.
            Employees already marked (Present, Absent, Leave, etc.) will not be changed.
          </Typography>
          <TextField
            select fullWidth size="small" label="Department *"
            value={weekOffDeptPick}
            onChange={e => setWeekOffDeptPick(e.target.value)}>
            <MenuItem value="">— Select Department —</MenuItem>
            {departments.map(d => (
              <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setWeekOffDeptDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleMarkWeekOffConfirm} variant="contained"
            sx={{ background: 'linear-gradient(135deg,#00695c,#00897b)', borderRadius: 2, fontWeight: 700 }}>
            Mark Week Off
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
